import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import EventLayout from "@/components/event/EventLayout";
import { eventApi, humanDay } from "@/lib/eventHub";
import {
  ChevronLeft, ScanLine, BookOpen, Camera, Heart, MapPin,
  Users, Rocket, CalendarDays, ArrowRight, Sparkles, Loader2,
} from "lucide-react";

/**
 * Day-specific Event Hub — clicking the "Friday VIP Experience" or "Saturday
 * Business Showers" card on /event lands here. Every action on this page is
 * scoped to the selected day so a Friday attendee can never accidentally
 * check in Saturday (see spec §2).
 */
export default function EventDayHub() {
  const { day: slug } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let m = true;
    (async () => { try { const c = await eventApi.config(); if (m) setConfig(c.data); } catch { /* noop */ } })();
    return () => { m = false; };
  }, []);

  const day = useMemo(() => {
    if (!config?.event) return null;
    if (slug === "friday") return { ...config.event.friday, slug: "friday", accent: "fuchsia" };
    if (slug === "saturday") return { ...config.event.saturday, slug: "saturday", accent: "emerald" };
    return null;
  }, [config, slug]);

  if (!config) {
    return (
      <EventLayout showProgramBanner={false}>
        <div className="p-16 text-center text-emerald-800/50"><Loader2 className="mx-auto animate-spin" /></div>
      </EventLayout>
    );
  }
  if (!day) {
    return (
      <EventLayout showProgramBanner={false}>
        <div className="mx-auto max-w-lg p-8 text-center">
          <div className="font-[Cormorant_Garamond] text-3xl text-emerald-950">Day not found</div>
          <Link to="/event" data-testid="day-notfound-back" className="mt-4 inline-block rounded-full bg-emerald-950 text-white px-4 h-10 leading-10 text-sm font-[Outfit] font-bold">
            Back to Event Hub
          </Link>
        </div>
      </EventLayout>
    );
  }

  const isFriday = day.slug === "friday";
  const isDark = day.accent === "fuchsia";
  const photos = config.photos || {};
  const donation = config.donation || {};

  return (
    <EventLayout showProgramBanner={false} className="bg-gradient-to-b from-white via-emerald-50/40 to-white">
      <button
        onClick={() => navigate("/event")}
        data-testid="dayhub-back"
        className="mx-auto max-w-lg w-full px-5 pt-5 flex items-center gap-1 text-xs font-[Outfit] font-bold uppercase tracking-widest text-emerald-800/60 hover:text-fuchsia-700"
      >
        <ChevronLeft size={14} /> All events
      </button>

      {/* HERO */}
      <section
        data-testid={`day-hero-${day.slug}`}
        className={`mx-4 mt-3 rounded-3xl overflow-hidden ${isDark ? "bg-fuchsia-600 text-white" : "bg-emerald-950 text-white"}`}
      >
        <div className="relative p-6 sm:p-8">
          <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 border border-white/20 px-3 py-1 mb-3">
              <Rocket size={11} className={isDark ? "text-fuchsia-100" : "text-fuchsia-300"} />
              <span className="text-[10px] tracking-[0.24em] uppercase font-[Outfit] font-bold">
                {isFriday ? "Day 1 · VIP Experience" : "Day 2 · Main Event"}
              </span>
            </div>
            <h1 className="font-[Cormorant_Garamond] text-4xl sm:text-5xl leading-[1.02]">
              {day.name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm font-[Outfit] text-white/85">
              <span className="inline-flex items-center gap-1.5"><CalendarDays size={14} /> {humanDay(day.date)}</span>
              <span className="text-white/40">·</span>
              <span className="font-bold">{day.times}</span>
            </div>
          </div>
        </div>
      </section>

      {/* PRIMARY ACTIONS */}
      <div className="mx-auto max-w-lg px-5 pt-6 space-y-3">
        {/* Check In — always primary */}
        <Link
          to={`/event/checkin?day=${day.date}`}
          data-testid={`dayhub-checkin-${day.slug}`}
          className="block rounded-2xl bg-emerald-950 text-white px-5 py-4 shadow-lg active:scale-[0.99] transition-transform"
        >
          <div className="flex items-center gap-3">
            <span className="h-11 w-11 rounded-xl bg-fuchsia-500 text-white flex items-center justify-center shrink-0"><ScanLine size={20} /></span>
            <div className="flex-1">
              <div className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-fuchsia-300">Fast Check-In</div>
              <div className="font-[Cormorant_Garamond] text-2xl leading-tight">Check In for {isFriday ? "Friday" : "Saturday"}</div>
              <div className="mt-0.5 text-[11px] font-[Outfit] text-white/60">{day.times}</div>
            </div>
            <ArrowRight size={18} className="text-white/60 shrink-0" />
          </div>
        </Link>

        {/* Guest of a host */}
        <Link
          to={`/event/guest-checkin?day=${day.date}`}
          data-testid={`dayhub-guest-${day.slug}`}
          className="block rounded-2xl border-2 border-fuchsia-200 bg-fuchsia-50/40 hover:border-fuchsia-400 px-4 py-3 flex items-center gap-3 transition-colors"
        >
          <span className="h-10 w-10 rounded-xl bg-fuchsia-100 text-fuchsia-700 flex items-center justify-center shrink-0"><Users size={16} /></span>
          <div className="flex-1 min-w-0">
            <div className="font-[Cormorant_Garamond] text-lg leading-tight text-emerald-950">I am a guest of a host</div>
            <div className="text-[11px] font-[Outfit] text-emerald-950/60">Tap to find your host</div>
          </div>
          <ArrowRight size={16} className="text-emerald-950/30 shrink-0" />
        </Link>

        {/* Program booklet */}
        <Link
          to={`/event/program?day=${day.date}`}
          data-testid={`dayhub-program-${day.slug}`}
          className="block rounded-2xl border-2 border-emerald-100 bg-white hover:border-emerald-300 px-4 py-3 flex items-center gap-3 transition-colors"
        >
          <span className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0"><BookOpen size={16} /></span>
          <div className="flex-1 min-w-0">
            <div className="font-[Cormorant_Garamond] text-lg leading-tight text-emerald-950">Open {isFriday ? "Friday" : "Saturday"} Program Booklet</div>
            <div className="text-[11px] font-[Outfit] text-emerald-950/60">Speakers, honorees, run-of-show</div>
          </div>
          <ArrowRight size={16} className="text-emerald-950/30 shrink-0" />
        </Link>

        {/* Photos */}
        {(photos.upload_url || photos.gallery_url) && (
          <Link
            to="/event/photos"
            data-testid={`dayhub-photos-${day.slug}`}
            className="block rounded-2xl border-2 border-emerald-100 bg-white hover:border-emerald-300 px-4 py-3 flex items-center gap-3 transition-colors"
          >
            <span className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0"><Camera size={16} /></span>
            <div className="flex-1 min-w-0">
              <div className="font-[Cormorant_Garamond] text-lg leading-tight text-emerald-950">Share your photos & videos</div>
              <div className="text-[11px] font-[Outfit] text-emerald-950/60">Upload or view the Kululu gallery</div>
            </div>
            <ArrowRight size={16} className="text-emerald-950/30 shrink-0" />
          </Link>
        )}

        {/* Donation */}
        {donation.url && (
          <a
            href={donation.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`dayhub-donate-${day.slug}`}
            className="block rounded-2xl border-2 border-fuchsia-200 bg-white hover:border-fuchsia-400 px-4 py-3 flex items-center gap-3 transition-colors"
          >
            <span className="h-10 w-10 rounded-xl bg-fuchsia-100 text-fuchsia-700 flex items-center justify-center shrink-0"><Heart size={16} /></span>
            <div className="flex-1 min-w-0">
              <div className="font-[Cormorant_Garamond] text-lg leading-tight text-emerald-950">Support the mission</div>
              <div className="text-[11px] font-[Outfit] text-emerald-950/60">{donation.impact ? String(donation.impact).slice(0, 60) + (donation.impact.length > 60 ? "…" : "") : "Every gift underwrites coaching & community."}</div>
            </div>
            <ArrowRight size={16} className="text-emerald-950/30 shrink-0" />
          </a>
        )}

        {/* Sponsors */}
        <Link
          to="/event/sponsors"
          data-testid={`dayhub-sponsors-${day.slug}`}
          className="block rounded-2xl border-2 border-emerald-100 bg-white hover:border-emerald-300 px-4 py-3 flex items-center gap-3 transition-colors"
        >
          <span className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0"><Sparkles size={16} /></span>
          <div className="flex-1 min-w-0">
            <div className="font-[Cormorant_Garamond] text-lg leading-tight text-emerald-950">Meet sponsors & VIP panel</div>
            <div className="text-[11px] font-[Outfit] text-emerald-950/60">The community behind the Business Showers</div>
          </div>
          <ArrowRight size={16} className="text-emerald-950/30 shrink-0" />
        </Link>

        {/* Venue info */}
        <div className="rounded-2xl bg-emerald-50/60 border border-emerald-100 p-4">
          <div className="flex items-start gap-3">
            <span className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0"><MapPin size={16} /></span>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-fuchsia-700">Event Location</div>
              <div className="font-[Cormorant_Garamond] text-lg leading-tight text-emerald-950">Charlotte, NC</div>
              <div className="text-[11px] font-[Outfit] text-emerald-950/60 mt-0.5">
                Venue details and directions will appear here closer to the event. Contact <a className="underline" href="mailto:hello@fimbusinessshowers.org">hello@fimbusinessshowers.org</a> with questions.
              </div>
            </div>
          </div>
        </div>

        <p className="text-[11px] font-[Outfit] text-center text-emerald-950/50 pt-2 pb-4">
          Attending both nights? You’ll check in separately for each day.
        </p>
      </div>
    </EventLayout>
  );
}
